import * as React from 'react';
import {
    Checkbox,
    DefaultButton,
    Dialog,
    DialogFooter,
    DialogType,
    MessageBar,
    MessageBarType,
    PrimaryButton,
    Stack,
    Text,
} from '@fluentui/react';
import type { Service } from '../../interfaces/Service';
import {
    createBulkPullRequestReminderEmailDraft,
    downloadRegressionEmailDraft,
} from '../../features/regression-email/create-regression-email';
import type { AutoPrSignal } from './types';
import { buildAdoPullRequestUrl } from './helpers';
import {
    getSignedInUserDisplayName,
    resolveReviewerEmailAddresses,
} from './reviewerEmailResolution';

const REQUIRED_REVIEWER = 3;
const REVIEWER_SIGNOFF = 4;

interface ReviewerChoice {
    signalKey: string;
    name: string;
    emailAddress: string | null;
    isRequired: boolean;
    isSatisfied: boolean;
    state: string | null;
}

function buildReviewerChoices(signals: AutoPrSignal[]): ReviewerChoice[] {
    const choices = new Map<string, ReviewerChoice>();
    signals
        .filter(signal =>
            signal.category === REQUIRED_REVIEWER || signal.category === REVIEWER_SIGNOFF)
        .forEach(signal => {
            const name = signal.displayName?.trim() || signal.name.trim();
            if (!name) return;
            choices.set(signal.signalKey, {
                signalKey: signal.signalKey,
                name,
                emailAddress: signal.emailAddress?.trim() || null,
                isRequired: signal.isRequired === true,
                isSatisfied: signal.isSatisfied === true,
                state: signal.state?.trim() || null,
            });
        });
    return [...choices.values()].sort((left, right) => {
        const leftRank = left.isRequired && !left.isSatisfied ? 0 : left.isRequired ? 1 : 2;
        const rightRank = right.isRequired && !right.isSatisfied ? 0 : right.isRequired ? 1 : 2;
        return leftRank - rightRank || left.name.localeCompare(right.name);
    });
}

function reviewerStatus(choice: ReviewerChoice) {
    if (choice.isRequired && !choice.isSatisfied) return 'Required - signoff pending';
    if (choice.isRequired) return 'Required - signed off';
    if (choice.isSatisfied) return 'Reviewer - signed off';
    return choice.state ? `Reviewer - ${choice.state}` : 'Reviewer';
}

export function PullRequestReviewerEmailButton(props: {
    service: Service | undefined;
    taskListId: number;
    taskId: number;
    prId: number;
    scope: string;
    attentionSummary: string;
    dateCreated?: string | null;
    signals: AutoPrSignal[];
    reviewInspectionComplete?: boolean;
    disabled?: boolean;
}) {
    const {
        service,
        taskListId,
        taskId,
        prId,
        scope,
        attentionSummary,
        dateCreated,
        signals,
        reviewInspectionComplete,
        disabled = false,
    } = props;
    const [isOpen, setIsOpen] = React.useState(false);
    const [isCreating, setIsCreating] = React.useState(false);
    const [selectedSignalKeys, setSelectedSignalKeys] = React.useState<Set<string>>(new Set());
    const [message, setMessage] = React.useState<string | null>(null);
    const choices = React.useMemo(() => buildReviewerChoices(signals), [signals]);
    const defaultSelectionKey = React.useMemo(
        () => choices
            .filter(choice => choice.isRequired && !choice.isSatisfied)
            .map(choice => choice.signalKey)
            .join('\u001f'),
        [choices],
    );
    const prUrl = buildAdoPullRequestUrl(service, prId);

    React.useEffect(() => {
        if (!isOpen) return;
        setSelectedSignalKeys(new Set(defaultSelectionKey
            ? defaultSelectionKey.split('\u001f')
            : []));
        setMessage(null);
    }, [defaultSelectionKey, isOpen]);

    function toggleReviewer(signalKey: string, checked: boolean) {
        setSelectedSignalKeys(current => {
            const next = new Set(current);
            if (checked) next.add(signalKey);
            else next.delete(signalKey);
            return next;
        });
    }

    async function createDraft() {
        if (disabled) return;
        if (!prUrl) {
            setMessage('Could not build the Azure DevOps URL for this pull request.');
            return;
        }
        const selected = choices.filter(choice =>
            selectedSignalKeys.has(choice.signalKey));
        if (selected.length === 0) {
            setMessage('Select at least one reviewer.');
            return;
        }

        setIsCreating(true);
        try {
            const resolution = await resolveReviewerEmailAddresses(
                service?.name ?? '',
                selected.map(choice => ({
                    signalKey: choice.signalKey,
                    name: choice.name,
                    emailAddress: choice.emailAddress,
                    pullRequests: [{ taskListId, taskId, prId }],
                })),
            );
            const createdAt = dateCreated ? Date.parse(dateCreated) : Number.NaN;
            const ageDays = Number.isNaN(createdAt)
                ? null
                : Math.max(0, Math.floor((Date.now() - createdAt) / 86400000));
            const draft = createBulkPullRequestReminderEmailDraft({
                senderName: getSignedInUserDisplayName(),
                reviewers: resolution.reviewers.map(reviewer => ({
                    name: reviewer.name,
                    email: reviewer.emailAddress,
                })),
                pullRequests: [{
                    prId,
                    scope,
                    prUrl,
                    attentionSummary,
                    ageDays,
                }],
            });
            downloadRegressionEmailDraft(draft);
            const unresolvedCount = resolution.reviewers.filter(
                reviewer => !reviewer.emailAddress,
            ).length;
            const lookupWarning = resolution.lookupFailed
                ? ' The secondary email lookup failed; unresolved names remain in the To field.'
                : unresolvedCount > 0
                    ? ' Resolve the remaining recipient name in Outlook before sending.'
                    : '';
            setMessage(`Downloaded a reminder draft for ${selected.length} reviewer${
                selected.length === 1 ? '' : 's'
            }.${lookupWarning}`);
        } catch (reason) {
            setMessage(reason instanceof Error
                ? reason.message
                : 'Could not create the reviewer reminder draft.');
        } finally {
            setIsCreating(false);
        }
    }

    return (
        <>
            <DefaultButton
                text="Email reviewers"
                disabled={disabled || choices.length === 0 || !prUrl}
                onClick={() => setIsOpen(true)}
            />
            <Dialog
                hidden={!isOpen}
                onDismiss={() => setIsOpen(false)}
                dialogContentProps={{
                    type: DialogType.largeHeader,
                    title: `Email reviewers for PR ${prId}`,
                    subText: 'Outstanding required reviewers are selected by default. You can include other known reviewers.',
                }}
                modalProps={{ isBlocking: false }}
                minWidth={560}
            >
                <Stack tokens={{ childrenGap: 8 }}>
                    {reviewInspectionComplete === false && (
                        <MessageBar messageBarType={MessageBarType.warning}>
                            The latest reviewer inspection did not complete, so this list may be incomplete.
                        </MessageBar>
                    )}
                    {choices.length === 0 ? (
                        <Text>No persisted reviewer information is available for this PR.</Text>
                    ) : choices.map(choice => (
                        <div key={choice.signalKey}>
                            <Checkbox
                                label={`${choice.name} - ${reviewerStatus(choice)}`}
                                checked={selectedSignalKeys.has(choice.signalKey)}
                                onChange={(_, checked) =>
                                    toggleReviewer(choice.signalKey, checked === true)}
                            />
                            {!choice.emailAddress && (
                                <Text block variant="small" styles={{ root: { marginLeft: 26 } }}>
                                    Email unavailable; Guardian will try a secondary lookup when creating the draft.
                                </Text>
                            )}
                        </div>
                    ))}
                    {message && <MessageBar>{message}</MessageBar>}
                </Stack>
                <DialogFooter>
                    <PrimaryButton
                        text={isCreating ? 'Creating draft...' : 'Create email draft'}
                        disabled={selectedSignalKeys.size === 0 || isCreating}
                        onClick={createDraft}
                    />
                    <DefaultButton text="Cancel" onClick={() => setIsOpen(false)} />
                </DialogFooter>
            </Dialog>
        </>
    );
}
